import moment from "moment";
import React, { FC, useState } from "react";

import {
  useGetPortalInvoicePdfDataMutation,
  useGetPortalInvoicesQuery,
} from "../../../slices/PortalSlice";
import {
  buildInvoicePdf,
  invoiceFileName,
} from "../../billing/invoices/invoicePdf";
import { PortalInvoice } from "../portal.types";
import { errorMessage } from "../portal.utils";

export const money = (v: number) =>
  `$${(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const pill: Record<PortalInvoice["status"], string> = {
  Paid: "bg-[#E9F9F1] text-[#147A4B]",
  "Partially paid": "bg-[#E8F1FF] text-[#1D5BD8]",
  Overdue: "bg-[#FDECEC] text-[#B42318]",
  "Not due yet": "bg-[#F1F2F5] text-[#3A4256]",
};

/** Portal home: invoices the firm chose to show, with the same PDF the firm sends. */
export const PortalInvoicesCard: FC<{
  customerId: string;
  onError: (text: string) => void;
}> = ({ customerId, onError }) => {
  const { data } = useGetPortalInvoicesQuery(customerId, {
    refetchOnMountOrArgChange: true,
  });
  const [getPdfData] = useGetPortalInvoicePdfDataMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const list = data ?? [];
  if (!list.length) return null;
  const due = list.reduce(
    (sum, i) => sum + (i.status === "Paid" ? 0 : i.balance),
    0,
  );
  const visible = showAll ? list : list.slice(0, 5);

  const download = async (invoice: PortalInvoice) => {
    setBusy(invoice.id);
    try {
      const result = await getPdfData({ customerId, invoiceId: invoice.id });
      if ("error" in result) return onError(errorMessage(result.error));
      const { logoDataUrl, clientName, ...rest } = result.data;
      const fileName = invoiceFileName(clientName);
      const doc = await buildInvoicePdf({
        ...rest,
        logoUrl: logoDataUrl,
        fileTitle: fileName.replace(".pdf", ""),
      });
      doc.save(fileName);
    } catch {
      onError("The invoice couldn't be downloaded. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className={
        "flex flex-col gap-3 rounded-[14px] border border-[#E8E8EC] bg-white p-[18px] sm:p-6"
      }>
      <div className={"flex flex-wrap items-baseline justify-between gap-2"}>
        <h2 className={"m-0 text-base font-extrabold sm:text-[17px]"}>
          Invoices
        </h2>
        <span className={"text-[13px] font-semibold text-[#6B7185]"}>
          {due > 0 ? (
            <>
              Balance due{" "}
              <strong className={"text-[#1F2433]"}>{money(due)}</strong>
            </>
          ) : (
            "All paid. Thank you!"
          )}
        </span>
      </div>
      <ul className={"m-0 flex list-none flex-col gap-2 p-0"}>
        {visible.map((i) => (
          <li
            key={i.id}
            className={
              "flex flex-col gap-2.5 rounded-[12px] border border-[#EDEFF3] px-4 py-3 sm:flex-row sm:items-center sm:gap-3"
            }>
            <div className={"flex min-w-0 flex-1 items-start gap-3"}>
              <div className={"flex min-w-0 flex-1 flex-col"}>
                <span className={"text-sm font-bold"}>
                  Invoice #{i.invoiceNo}
                </span>
                <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
                  {i.invoiceDate
                    ? moment(i.invoiceDate).format("MMM D, YYYY")
                    : ""}
                  {i.dueDate && i.status !== "Paid"
                    ? ` · due ${moment(i.dueDate).format("MMM D, YYYY")}`
                    : ""}
                </span>
              </div>
              <div className={"flex shrink-0 flex-col items-end"}>
                <span className={"text-sm font-extrabold"}>
                  {money(i.status === "Paid" ? i.total : i.balance)}
                </span>
                {i.status === "Partially paid" && (
                  <span
                    className={"text-[11.5px] font-semibold text-[#6B7185]"}>
                    of {money(i.total)}
                  </span>
                )}
              </div>
            </div>
            <div
              className={
                "flex items-center justify-between gap-3 sm:justify-end"
              }>
              <span
                className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-bold ${
                  pill[i.status]
                }`}>
                {i.status}
              </span>
              <button
                type={"button"}
                onClick={() => download(i)}
                disabled={busy === i.id}
                className={
                  "h-9 whitespace-nowrap rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[13px] font-bold text-[#3A4256] hover:bg-[#F7F8FA] disabled:opacity-60"
                }>
                {busy === i.id ? "Preparing…" : "Download PDF"}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {list.length > 5 && (
        <button
          type={"button"}
          className={"self-start text-[13px] font-bold text-[#6B4EFF]"}
          onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show less" : `Show all ${list.length} invoices`}
        </button>
      )}
      <span className={"text-[12px] font-medium text-[#6B7185]"}>
        To pay, use the payment details on the invoice or contact your firm.
      </span>
    </section>
  );
};
